<div class="customer-view-container p-6">
    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
        <div class="flex items-center gap-4">
            <button type="button" (click)="goBack()"
                class="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
                <svg class="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                </svg>
                Back
            </button>
            <div>
                <h1 class="text-2xl font-semibold text-gray-900">{{ customer()?.name || 'Customer Details' }}</h1>
                <p class="text-sm text-gray-600 mt-1">View and manage customer information</p>
            </div>
        </div>
        @if (customer()) {
        <div class="flex gap-2">
            <jjames-button variant="secondary" iconLeft="fas fa-user-plus" (clicked)="createOperator()">
                Create Operator
            </jjames-button>
            <!-- <jjames-button variant="primary" iconLeft="fas fa-edit" (clicked)="editCustomer()">
        Edit Customer
      </jjames-button> -->
        </div>
        }
    </div>

    <!-- Loading State -->
    @if (loading()) {
    <div class="flex justify-center items-center py-12">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
    </div>
    }

    <!-- Error Message -->
    @if (errorMessage()) {
    <div class="mb-6">
        <jjames-alert type="error" [dismissible]="true" (dismissed)="errorMessage.set(null)">
            {{ errorMessage() }}
        </jjames-alert>
    </div>
    }

    <!-- Customer Details -->
    @if (!loading() && customer()) {
    <div class="space-y-6">
        <!-- Customer Information Card -->
        <jjames-card>
            <div class="px-6 py-4 border-b border-gray-200">
                <h2 class="text-lg font-medium text-gray-900">Customer Information</h2>
            </div>
            <div class="px-6 py-4">
                <dl class="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <dt class="text-sm font-medium text-gray-500">Customer Name</dt>
                        <dd class="mt-1 text-sm text-gray-900">{{ customer()?.name }}</dd>
                    </div>
                    <div>
                        <dt class="text-sm font-medium text-gray-500">Customer ID</dt>
                        <dd class="mt-1 text-sm text-gray-900 font-mono">{{ customer()?.id }}</dd>
                    </div>
                    <div>
                        <dt class="text-sm font-medium text-gray-500">Created At</dt>
                        <dd class="mt-1 text-sm text-gray-900">{{ customer()?.createdAt | date:'medium' }}</dd>
                    </div>
                    <div>
                        <dt class="text-sm font-medium text-gray-500">Last Updated</dt>
                        <dd class="mt-1 text-sm text-gray-900">{{ customer()?.updatedAt | date:'medium' }}</dd>
                    </div>
                </dl>
            </div>
        </jjames-card>

        <!-- Statistics Card -->
        <jjames-card>
            <div class="px-6 py-4 border-b border-gray-200">
                <h2 class="text-lg font-medium text-gray-900">Statistics</h2>
            </div>
            <div class="px-6 py-4">
                <div class="grid grid-cols-2 md:grid-cols-4 gap-6">
                    <div class="text-center">
                        <div class="text-3xl font-bold text-indigo-600">{{ customer()?.activeOperatorGroupCount || 0 }}
                        </div>
                        <div class="text-sm text-gray-500 mt-1">Active Groups</div>
                    </div>
                    <div class="text-center">
                        <div class="text-3xl font-bold text-blue-600">{{ customer()?.totalOperatorCount || 0 }}</div>
                        <div class="text-sm text-gray-500 mt-1">Total Operators</div>
                    </div>
                    <div class="text-center">
                        <div class="text-3xl font-bold text-green-600">{{ customer()?.adminGroupCount || 0 }}</div>
                        <div class="text-sm text-gray-500 mt-1">Admins</div>
                    </div>
                    <div class="text-center">
                        <div class="text-3xl font-bold text-purple-600">{{ customer()?.memberGroupCount || 0 }}</div>
                        <div class="text-sm text-gray-500 mt-1">Members</div>
                    </div>
                </div>
            </div>
        </jjames-card>

        <!-- Operator Groups Card -->
        <jjames-card>
            <div class="px-6 py-4 border-b border-gray-200">
                <h2 class="text-lg font-medium text-gray-900">Operator Groups</h2>
                <p class="text-sm text-gray-500 mt-1">Manage access groups and their members</p>
            </div>
            <div class="px-6 py-4">
                @if (customer()?.operatorGroups && customer()!.operatorGroups!.length > 0) {
                <div class="space-y-4">
                    @for (group of customer()?.operatorGroups; track group.id) {
                    <div class="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                        <div class="flex items-start justify-between">
                            <div class="flex-1">
                                <div class="flex items-center gap-3">
                                    <h3 class="text-base font-medium text-gray-900">{{ group.name }}</h3>
                                    <span
                                        [class]="'px-2 py-1 text-xs font-medium rounded-full ' + getGroupRoleBadgeClass(group.role)">
                                        {{ group.role }}
                                    </span>
                                    @if (group.hasAdministrativePrivileges) {
                                    <span
                                        class="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800">
                                        <svg class="inline h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                            <path fill-rule="evenodd"
                                                d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                                clip-rule="evenodd" />
                                        </svg>
                                        Admin Access
                                    </span>
                                    }
                                </div>
                                @if (group.description) {
                                <p class="text-sm text-gray-600 mt-1">{{ group.description }}</p>
                                }
                                <div class="flex items-center gap-6 mt-3 text-sm text-gray-500">
                                    <div class="flex items-center">
                                        <svg class="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                        </svg>
                                        {{ group.activeMemberCount || 0 }} active members
                                    </div>
                                    <div class="flex items-center">
                                        <svg class="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
                                        </svg>
                                        {{ group.assignedParkingSpaceCount || 0 }} parking spaces
                                    </div>
                                    @if (group.canManageOperatorGroups) {
                                    <div class="flex items-center text-indigo-600">
                                        <svg class="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                            <path
                                                d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                                        </svg>
                                        Can manage groups
                                    </div>
                                    }
                                    @if (group.canManageParkingSpaces) {
                                    <div class="flex items-center text-green-600">
                                        <svg class="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                            <path fill-rule="evenodd"
                                                d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z"
                                                clip-rule="evenodd" />
                                        </svg>
                                        Can manage parking
                                    </div>
                                    }
                                </div>
                            </div>
                            <div class="ml-4">
                                <button type="button" (click)="viewGroupMembers(group.id)"
                                    class="text-indigo-600 hover:text-indigo-900 text-sm font-medium flex items-center">
                                    View Members
                                    <svg class="h-4 w-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M9 5l7 7-7 7" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                    }
                </div>
                } @else {
                <div class="text-center py-8 text-gray-500">
                    <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <p class="mt-2">No operator groups found</p>
                </div>
                }
            </div>
        </jjames-card>
    </div>
    }
</div>